import {getTestProps} from '@benchling/js-test-utils';
import {compact} from '@benchling/js-utils';
import {css} from 'aphrodite';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  Background,
  Edge,
  OnConnect,
  OnConnectStartParams,
  ReactFlow,
  useReactFlow,
  useStore,
  Connection,
} from 'reactflow';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin, WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {
  ProcessFlowchartNodeDetailsType,
  ProcessFlowchartPlatformFeatureType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {SchemaById} from 'lookup-config/LookupConfigModalConstants';
import ReactFlowWrapper from 'processes/graph-builder/flowchart-viz/ReactFlowWrapper';
import {isValidConnection} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/edges/connectionValidationUtils';
import {
  getValueForFlowchartFeaturesContext,
  isFlowchartFeatureEnabled,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/FlowchartFeaturesContext';
import {HandlesContextValue} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/HandlesContext';
import {
  ProcessFlowchartTaskSchemaNodeAdapter,
  ProcessFlowchartNodeAdapter,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/ProcessFlowchartNodeAdapter';
import {NodesContextValue} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/NodesContext';
import {ProcessFlowchartEditorConfigurator} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorConfigurator';
import {
  FlowchartEditorMode,
  ProcessFlowchartEditorModeFeatureType,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';
import {DEFAULT_FIT_VIEW_OPTIONS} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import {
  PROCESS_FLOWCHART_EDGE_LABEL_SIZE,
  PROCESS_FLOWCHART_EDGE_LABEL_WEIGHT,
} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import {PLUGIN_REGISTRY_INSTANCE} from 'processes/graph-builder-processes/platform/PluginRegistry';
import {ProcessFlowchartValidationError} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {parseHandleId} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/flowchartIdentifierUtils';
import {FONT_FAMILY_PROXIMA} from 'styles/FontConstants';
import useStateIfMounted from 'ui-library/hooks/useStateIfMounted';
import DefaultBackground from 'ui-library/reactflow/backgrounds/DefaultBackground';
import {ControlledAutoLayoutButton} from 'ui-library/reactflow/controls/AutoLayoutControlButton';
import HorizontalControls, {FIT_VIEW_MAX_ZOOM} from 'ui-library/reactflow/controls/HorizontalControls';
import {useGetNodeHandlesData} from 'ui-library/reactflow/handles/useGetNodeHandlesData';
import {LayoutAlgorithmName} from 'ui-library/reactflow/layout/algorithms';
import {LayoutSettings} from 'ui-library/reactflow/layout/calculateDimensionsForLayout';
import {hideFlowchartElementBeforeLayout} from 'ui-library/reactflow/layout/hideFlowchartElementBeforeLayoutUtils';
import layoutFlowchart from 'ui-library/reactflow/layout/layoutFlowchart';
import {useDefaultReactFlowProps} from 'ui-library/reactflow/ReactFlowConstants';
import {SidepanelSize} from 'ui-library/reactflow/sidepanels/FlowchartContextPanel';
import {FLOWCHART_SIDE_PANEL_WIDTH} from 'ui-library/reactflow/sidepanels/FlowchartSidebar';
import {useFlowchartSidepanelManagerContext} from 'ui-library/reactflow/sidepanels/sidepanel-manager/FlowchartSidepanelUnsavedChangesContext';
import {getTextDimensions} from 'util/getTextDimensions';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {getAdapterForNode, getAdapterForTaskSchemaNode} from './adapterUtils';
import {CanvasContextProviders} from './CanvasContextProviders';
import {ProcessFlowchartBanners} from './ProcessFlowchartBanners';
import {
  CustomMappingData,
  DEFAULT_FLOWCHART_PROPS,
  EDITABLE_FLOWCHART_PROPS,
  PROCESS_FLOWCHART_CONFIG_EDITOR_STYLES,
  ProcessFlowchartEdgeType,
  ProcessFlowchartNodeType,
  READ_ONLY_FLOWCHART_PROPS,
  SNAP_GRID,
  StaticTemplateFlowchart,
  TaskDefinitionVersionSpecWithTaskSchemaFlowchart,
  TaskSchemaNodeSectionType,
  TransitionSource,
  UserFacingNodeTypes,
} from './processFlowchartConfigEditorConstants';
import ProcessFlowchartConfigEditorLeftSidepanel, {
  LEFT_SIDEPANEL_WIDTH,
} from './ProcessFlowchartConfigEditorLeftSidepanel';
import {convertStaticFlowchartToSpecFlowchartWithTaskSchema} from './processFlowchartConfigEditorNetworkUtils';
import {
  FlowchartNode,
  isInteractivePlaceholderNode,
  isInteractiveTaskNode,
  isStaticPlaceholderNodeData,
  isStaticTaskSchemaNodeData,
  TaskDefinitionVersionSpecWithTaskSchema,
  TaskSchemaNode,
  TaskSchemaStaticNodeData,
} from './processFlowchartConfigEditorNodeTypes';
import {
  PROCESS_FLOWCHART_CONFIG_EDITOR_EDGE_TYPES,
  PROCESS_FLOWCHART_CONFIG_EDITOR_NODE_TYPES,
  PROCESS_FLOWCHART_CONFIG_EDITOR_NODE_TYPES_BY_EXECUTION_TYPE,
  PROCESS_FLOWCHART_CONFIG_EDITOR_NODE_TYPES_BY_PLATFORM_FEATURE,
} from './processFlowchartConfigEditorRegistry';
import ProcessFlowchartConfigEditorRightSidepanel from './ProcessFlowchartConfigEditorRightSidepanel';
import {FlowchartNodeChange, FlowchartNodeChangeType} from './processFlowchartConfigEditorUtils';
import {FlowchartConfigWithMetadata} from './redux/flowchartConfigAutosaveManagerCallbacks';
import {
  FlowchartConfigAutosaverPausedCondition,
  FlowchartConfigMetadata,
} from './redux/reduceFlowchartConfig';
import {SingletonTaskSchemasContext} from './SingletonTaskSchemasContext';
import {
  createTransitionChangeForCustomMapping,
  createTransitionChangeForTransitionSource,
} from './types/transition/transitionChangeUtils';
import {ProcessTaskDefinitionsResult} from './useAllSchemasById';
import {
  FlowchartConfigWithPartialMetadata,
  LiveStateChanges,
} from './useFlowchartState/BaseFlowchartConfigStateManager';
import useFlowchartState from './useFlowchartState/useFlowchartState';

export interface ProcessFlowchartConfigEditorContentProps {
  getNodeByIdRef: React.MutableRefObject<(nodeId: string) => FlowchartNode | null>;
  flowchart: StaticTemplateFlowchart;
  flowchartEditorConfigurator: ProcessFlowchartEditorConfigurator;
  editorMode: FlowchartEditorMode;
  allSchemasById: SchemaById;
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  flowchartTaskDefinitionId: string;
  flowchartConfigId: string;
  taskDefinitionsResult: ProcessTaskDefinitionsResult;
  didInitialConfigHaveConditionalMapping: boolean;
  didInitialFlowchartHaveChainedRouters: boolean;
  refetchFlowchartConfig?: () => void;
  liveStateChanges: LiveStateChanges;
  locked: boolean;
  lockErrorMessage?: string;
  validationErrors: Array<ProcessFlowchartValidationError>;
  infoBannerMessages?: Array<string>;
  draftFlowchartConfigAndMetadata: FlowchartConfigWithMetadata;
  setDraftFlowchartConfigAndMetadata: (
    getNewFlowchartConfigAndMetadata: (
      currentFlowchartConfigAndMetadata: FlowchartConfigWithMetadata
    ) => FlowchartConfigWithPartialMetadata,
    saveImmediately?: boolean,
    skipSettingDirtyState?: boolean
  ) => Promise<FlowchartConfigMetadata | null>;
  ownerToRegistryMap?: Map<string, string>;
}

function getAllowedNodeTypes(
  flowchartEditorConfigurator: ProcessFlowchartEditorConfigurator
): Array<UserFacingNodeTypes> {
  const allowedTaskNodeTypes = compact(
    flowchartEditorConfigurator.allowedNodeExecutionTypes.map(
      (executionType) => PROCESS_FLOWCHART_CONFIG_EDITOR_NODE_TYPES_BY_EXECUTION_TYPE[executionType]
    )
  );
  const allowedNodeTypesForPlatformFeatures = compact(
    Object.entries(PROCESS_FLOWCHART_CONFIG_EDITOR_NODE_TYPES_BY_PLATFORM_FEATURE)
      .filter(([feature]) =>
        flowchartEditorConfigurator.hasFeature(feature as ProcessFlowchartPlatformFeatureType)
      )
      .map(([_, nodeType]) => nodeType)
  );
  return [...allowedTaskNodeTypes, ...allowedNodeTypesForPlatformFeatures];
}

/**
 * Horizontal flowchart config editor content that displays the specified flowchart config version.
 *
 * Converts static nodes and edges to interactive nodes and edges.
 */
export default function ProcessFlowchartConfigEditorContent(
  props: ProcessFlowchartConfigEditorContentProps
): JSX.Element {
  const defaultReactFlowProps = useDefaultReactFlowProps();
  const interactionProps = props.locked ? READ_ONLY_FLOWCHART_PROPS : EDITABLE_FLOWCHART_PROPS;
  const singletonTaskSchemasByName = SingletonTaskSchemasContext.useContext();

  const flowchartFeatures = useMemo(
    () =>
      getValueForFlowchartFeaturesContext(
        props.flowchartEditorConfigurator.platformFlowchartFeatures,
        props.editorMode
      ),
    [props.flowchartEditorConfigurator.platformFlowchartFeatures, props.editorMode]
  );

  const allowedNodeTypes = useMemo(
    () => getAllowedNodeTypes(props.flowchartEditorConfigurator),
    [props.flowchartEditorConfigurator]
  );

  const [_isAutosaverPaused, setIsAutosaverPaused] = props.liveStateChanges.isAutosaverPausedState;

  const reactFlowInstance = useReactFlow();
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const leftSidepanelRef = useRef<HTMLDivElement>(null);
  const rightSidepanelRef = useRef<HTMLDivElement>(null);
  const [rightPanelSize, setRightPanelSize] = useState<SidepanelSize | null>(null);

  const taskDefinitions = useMemo(() => props.taskDefinitionsResult ?? [], [props.taskDefinitionsResult]);

  const ptdvIdToTaskSchemaId = useMemo(() => {
    const map: Record<string, string> = {};
    for (const node of props.flowchart.nodesStaticData) {
      if (isStaticTaskSchemaNodeData(node)) {
        map[node.taskDefinitionVersionSpec.clientId] = node.taskSchemaId;
      }
    }
    for (const def of taskDefinitions) {
      const ptdvId = def.latestVersion.id;
      if (map[ptdvId] == null) {
        map[ptdvId] = def.latestVersion.processTaskSchema.id;
      }
    }
    return map;
  }, [props.flowchart.nodesStaticData, taskDefinitions]);

  const addNodeParams = useMemo(
    () => ({
      flowchartEditorConfigurator: props.flowchartEditorConfigurator,
      rootTaskSchema: props.rootTaskSchema,
      taskDefinitionsForSidepanel: taskDefinitions,
      didInitialFlowchartHaveChainedRouters: props.didInitialFlowchartHaveChainedRouters,
      singletonTaskSchemasByName,
    }),
    [
      props.flowchartEditorConfigurator,
      props.rootTaskSchema,
      taskDefinitions,
      props.didInitialFlowchartHaveChainedRouters,
      singletonTaskSchemasByName,
    ]
  );

  const {
    interactiveNodes,
    interactiveEdges,
    applyFlowchartChanges,
    onReactFlowNodeChangesOnly,
    onReactFlowEdgeChangesOnly,
    onConnect,
    setNodesAndEdgesViaChangesForLayout,
    // Sidepanel state
    isLeftSidepanelOpen,
    // Node addition state
    interactiveFlowchartOptions,
    setPlaceholderNodeBeingAdded,
    // Node dimensions
    getMeasuredDimensionsFromStore,
  } = useFlowchartState({
    staticFlowchart: props.flowchart,
    flowchartConfigMetadata: props.draftFlowchartConfigAndMetadata.metadata,
    validationErrors: props.validationErrors,
    setFlowchartAndMetadata: props.setDraftFlowchartConfigAndMetadata,
    addNodeParams,
    flowchartFeatures,
    reactFlowInstance,
    reactFlowWrapper,
    leftSidepanelRef,
    rightSidepanelRef,
    rightPanelSize,
    flowchartEditorConfigurator: props.flowchartEditorConfigurator,
  });

  // Update refs during render (not in useEffect) so they're always in sync with current values
  props.getNodeByIdRef.current = (nodeId: string) =>
    interactiveNodes.find((node) => node.id === nodeId) ?? null;

  const [connectionDragState, setConnectionDragState] =
    useState<HandlesContextValue['connectionDragState']>(null);
  const hideDataFlowEdges = useMemo(
    () =>
      BenchlingConfig.WORKFLOWS_MAX_DATA_FLOW_EDGES_VISIBLE_IN_UI <
      interactiveEdges.filter((edge) => edge.type === ProcessFlowchartEdgeType.DATA_FLOW).length,
    [interactiveEdges]
  );

  const hasPerformedInitialAutolayout = useRef(false);

  const [performInitialFitView, setPerformInitialFitView] = useStateIfMounted(false);

  // Record the start and end nodes for use in layout.
  const startNode = useMemo(
    () =>
      interactiveNodes.find(
        (node): node is TaskSchemaNode =>
          isInteractiveTaskNode(node) &&
          node.data.nodeDetailsType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS
      ) ?? null,
    [interactiveNodes]
  );

  const endNode = useMemo(
    () =>
      interactiveNodes.find(
        (node): node is TaskSchemaNode =>
          isInteractiveTaskNode(node) &&
          node.data.nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS
      ) ?? null,
    [interactiveNodes]
  );

  const getHandles = useGetNodeHandlesData();

  const allNodesReadyForAutolayout = useStore(
    useCallback(() => {
      if (hasPerformedInitialAutolayout.current || interactiveNodes.length === 0) {
        return false;
      }

      // Check if all nodes have handles. In testing we found that on mount the reactflow nodes
      // are not always finished initializing. Checking handles is a proxy for us to determine if node
      // state is fully initialized.
      const nodesWithoutHandles = interactiveNodes.filter((node) => getHandles(node.id).length === 0);

      return nodesWithoutHandles.length === 0;
    }, [interactiveNodes, getHandles])
  );

  // Used by layout system to get the dimensions of edge labels.
  // todo(BNCH-137336): Possibly make this method for calculating label dimensions the default
  // for our internal ReactFlow library.
  const calculateEdgeLabelDimensions = useCallback((edge: Edge): {width: number; height: number} => {
    if (edge.label == null || typeof edge.label !== 'string') {
      return {width: 0, height: 0};
    }
    const {width, height} = getTextDimensions(
      edge.label,
      PROCESS_FLOWCHART_EDGE_LABEL_WEIGHT,
      PROCESS_FLOWCHART_EDGE_LABEL_SIZE,
      FONT_FAMILY_PROXIMA
    );

    return {
      width,
      height,
    };
  }, []);

  const getCustomLayoutSettings = useCallback(
    (): Partial<LayoutSettings<FlowchartNode, Edge>> => ({
      snapToGrid: SNAP_GRID,
      algorithm: LayoutAlgorithmName.ELK,
      startNodeId: startNode?.id,
      endNodeId: endNode?.id,
      calculateEdgeLabelDimensions,
      calculateNodeDimensions: (node) => getMeasuredDimensionsFromStore(node.id),
      horizontalSpaceBetweenNodes: 30,
    }),
    [startNode, endNode, calculateEdgeLabelDimensions, getMeasuredDimensionsFromStore]
  );

  useEffect(() => {
    // Only perform initial autolayout once, not every time nodes change
    if (interactiveNodes.length === 0 || hasPerformedInitialAutolayout.current) {
      return;
    }

    // Wait for all nodes to be ready (handles + dimensions) before running autolayout.
    if (!allNodesReadyForAutolayout) {
      return;
    }

    const customLayoutSettings = getCustomLayoutSettings();

    layoutFlowchart(
      {
        nodes: interactiveNodes,
        edges: interactiveEdges,
        getHandles,
      },
      customLayoutSettings
    ).then(({nodes: layoutNodes, edges: layoutEdges}) => {
      setNodesAndEdgesViaChangesForLayout(layoutNodes, layoutEdges);
      hasPerformedInitialAutolayout.current = true;
      setPerformInitialFitView(true);
    });
  }, [
    interactiveNodes,
    interactiveEdges,
    allNodesReadyForAutolayout,
    setNodesAndEdgesViaChangesForLayout,
    setPerformInitialFitView,
    getHandles,
    getCustomLayoutSettings,
  ]);

  useEffect(() => {
    if (!performInitialFitView) {
      return;
    }
    setTimeout(() => {
      reactFlowInstance.fitView({...DEFAULT_FIT_VIEW_OPTIONS, maxZoom: FIT_VIEW_MAX_ZOOM});
    });
    setPerformInitialFitView(false);
  }, [performInitialFitView, reactFlowInstance, setPerformInitialFitView]);

  // Hide nodes and edges before initial layout completes.
  // Note: `performInitialFitView` is included in the below dependencies as a proxy for when
  // `hasPerformedInitialAutolayout.current` changes. The ref is used in the layout effect but we need
  // these memos to recalculate when autolayout completes.
  const nodesToRender = useMemo(() => {
    if (hasPerformedInitialAutolayout.current) {
      return interactiveNodes;
    }
    return interactiveNodes.map((node) => hideFlowchartElementBeforeLayout(node));
    // `performInitialFitView` is an extra dependency, see note above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interactiveNodes, performInitialFitView]);

  const edgesToRender = useMemo(() => {
    let edgesToRender = interactiveEdges;
    if (hideDataFlowEdges) {
      edgesToRender = edgesToRender.filter((edge) => edge.type === ProcessFlowchartEdgeType.CONTROL_FLOW);
    }
    if (!hasPerformedInitialAutolayout.current) {
      return edgesToRender.map((edge) => hideFlowchartElementBeforeLayout(edge));
    }
    return edgesToRender;
    // `performInitialFitView` is an extra dependency, see note above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interactiveEdges, hideDataFlowEdges, performInitialFitView]);

  const handleConnectStart = useCallback(
    (event: React.MouseEvent | React.TouchEvent, params: OnConnectStartParams): void => {
      if (!params.handleId || !params.nodeId) {
        return;
      }

      const sourceHandleInfo = parseHandleId(params.handleId);
      setConnectionDragState({
        isDragging: true,
        sourceHandleInfo,
      });
    },
    []
  );

  const handleConnectEnd = useCallback((): void => {
    setConnectionDragState(null);
  }, []);
  const handleConnect: OnConnect = useCallback(
    (params: Connection) => {
      setConnectionDragState(null);

      if (
        params.source == null ||
        params.target == null ||
        params.sourceHandle == null ||
        params.targetHandle == null
      ) {
        return;
      }
      onConnect(params);
    },
    [onConnect]
  );

  const dragOverHandler = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
  };

  const hasPlaceholderNodes = useMemo(
    () => interactiveNodes.some((node) => isInteractivePlaceholderNode(node)),
    [interactiveNodes]
  );

  useEffect(() => {
    setIsAutosaverPaused(FlowchartConfigAutosaverPausedCondition.HAS_PLACEHOLDER_NODE, hasPlaceholderNodes);
  }, [hasPlaceholderNodes, setIsAutosaverPaused]);

  const taskDefinitionVersionBasedFlowchart = useMemo(
    () => convertStaticFlowchartToSpecFlowchartWithTaskSchema(props.flowchart),
    [props.flowchart]
  );

  const sidepanelManager = useFlowchartSidepanelManagerContext();
  const {nodeId: selectedNodeId, fieldId: selectedFieldId} = sidepanelManager.getEffectiveState();
  const selectedNode = useMemo(
    () => interactiveNodes.find((n) => n.id === selectedNodeId) ?? null,
    [interactiveNodes, selectedNodeId]
  );

  const handlesContext: HandlesContextValue = useMemo(
    () => ({
      connectionDragState,
      hideControlFlowEdges: false,
      hideDataFlowEdges,
    }),
    [connectionDragState, hideDataFlowEdges]
  );

  const validateConnection = useCallback(
    (connection: Connection): boolean => {
      const getAdapterForNodeId = (
        nodeId: string
      ): ProcessFlowchartTaskSchemaNodeAdapter<unknown, unknown> | ProcessFlowchartNodeAdapter | null => {
        const node = interactiveNodes.find((n) => n.id === nodeId) ?? null;
        return getAdapterForNode(node);
      };

      const getNodeData = (
        nodeId: string
      ): {nodeDetailsType?: ProcessFlowchartNodeDetailsType; nodeType?: ProcessFlowchartNodeType} | null => {
        const node = interactiveNodes.find((n) => n.id === nodeId);
        if (!node) {
          return null;
        }
        if (isStaticPlaceholderNodeData(node.data)) {
          return null;
        }

        return {nodeDetailsType: node.data.nodeDetailsType, nodeType: node.data.nodeType};
      };

      return isValidConnection(
        connection,
        interactiveEdges,
        getNodeData,
        getAdapterForNodeId,
        props.flowchartEditorConfigurator.platformFlowchartFeatures
      );
    },
    [interactiveEdges, interactiveNodes, props.flowchartEditorConfigurator.platformFlowchartFeatures]
  );

  const nodesContext: NodesContextValue = useMemo(
    () => ({
      nodes: interactiveNodes,
      startNode: startNode?.data ?? props.draftFlowchartConfigAndMetadata.metadata.rootNodeStaticData ?? null,
      endNode,
    }),
    [interactiveNodes, endNode, props.draftFlowchartConfigAndMetadata.metadata.rootNodeStaticData, startNode]
  );

  const onChangeCustomMappingData = useCallback(
    (edgeId: string, newCustomMappingData: CustomMappingData) => {
      applyFlowchartChanges((flowchartDraftState) => {
        const transitionChange = createTransitionChangeForCustomMapping({
          edgeId,
          customMappingData: newCustomMappingData,
          edges: flowchartDraftState.staticFlowchart.edgesStaticData,
          nodes: flowchartDraftState.staticFlowchart.nodesStaticData,
          transitions: flowchartDraftState.metadata.transitions,
        });

        return transitionChange ? {transitionChanges: [transitionChange]} : {};
      });
    },
    [applyFlowchartChanges]
  );

  const onChangeTransitionSource = useCallback(
    (nodeIdPair: string, newTransitionSource: TransitionSource) => {
      applyFlowchartChanges((flowchartDraftState) => {
        const transitionChange = createTransitionChangeForTransitionSource({
          nodeIdPair,
          transitionSource: newTransitionSource,
          nodes: flowchartDraftState.staticFlowchart.nodesStaticData,
          transitions: flowchartDraftState.metadata.transitions,
        });

        return transitionChange ? {transitionChanges: [transitionChange]} : {};
      });
    },
    [applyFlowchartChanges]
  );

  /**
   * Enables updates to a node from the right sidepanel to be saved immediately to the DB
   * without waiting for an auto save.
   *
   * If isBlocking is true, a network call will be made immediately to save the flowchart config.
   * If isBlocking is false, the node changes will be accumulated with other changes to wait for auto save.
   */
  const onUpdateTaskDefinitionVersion = useCallback(
    (
      updater: (
        currentFlowchart: TaskDefinitionVersionSpecWithTaskSchemaFlowchart
      ) => TaskDefinitionVersionSpecWithTaskSchema<unknown>,
      isBlocking: boolean
    ): Promise<FlowchartConfigMetadata | null> =>
      applyFlowchartChanges((flowchartDraftState) => {
        assert(selectedNode?.id != null, 'Expected selectedNode to be non-null');

        const selectedNodeData = flowchartDraftState.staticFlowchart.nodesStaticData.find(
          (node) => node.id === selectedNode.id
        );
        if (selectedNodeData == null) {
          return {};
        }
        assert(isStaticTaskSchemaNodeData(selectedNodeData), 'Expected node to be a static task node');

        const currentFlowchart = convertStaticFlowchartToSpecFlowchartWithTaskSchema(
          flowchartDraftState.staticFlowchart
        );

        let newTaskDefinitionVersionSpec = updater(currentFlowchart);
        const adapter = PLUGIN_REGISTRY_INSTANCE.getTaskSchemaNodeAdapter(
          selectedNodeData.taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.taskSchema.executionType
        );

        // Find all nodes that share the same task definition version and update them all.
        // For singleton schemas (like procedure unit operations), all nodes share the same taskSchemaId
        // but have unique task definition versions, so we must match by the definition version ID.
        const taskDefinitionVersionClientId = selectedNodeData.taskDefinitionVersionSpec.clientId;
        const nodesToUpdate = flowchartDraftState.staticFlowchart.nodesStaticData.filter(
          (node): node is TaskSchemaStaticNodeData =>
            isStaticTaskSchemaNodeData(node) &&
            node.taskDefinitionVersionSpec.clientId === taskDefinitionVersionClientId
        );

        newTaskDefinitionVersionSpec =
          adapter.updateTaskDefinitionVersionWithApplicationData?.(newTaskDefinitionVersionSpec) ??
          newTaskDefinitionVersionSpec;

        // Get all node names except for the nodes being updated
        const nodeIdsBeingUpdated = new Set(nodesToUpdate.map((node) => node.id));
        const existingNodeNames = flowchartDraftState.staticFlowchart.nodesStaticData
          .filter((node) => !nodeIdsBeingUpdated.has(node.id))
          .map((node) => node.nodeName);

        // Create node changes for all matching nodes
        const nodeChanges: Array<FlowchartNodeChange> = nodesToUpdate.map((nodeToUpdate) => {
          const newSections = adapter.createSections(
            newTaskDefinitionVersionSpec,
            nodeToUpdate.nodeDetailsType
          );

          const oldMapFromStart = nodeToUpdate.sections.find(
            (section) => section.sectionType === TaskSchemaNodeSectionType.MAP_FROM_START
          );
          const newMapFromStart = newSections.find(
            (section) => section.sectionType === TaskSchemaNodeSectionType.MAP_FROM_START
          );
          if (oldMapFromStart && newMapFromStart) {
            newMapFromStart.sectionRows = oldMapFromStart.sectionRows;
          }

          const nodeName = adapter.getNodeName(
            newTaskDefinitionVersionSpec,
            nodeToUpdate.nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS,
            existingNodeNames
          );

          // Add the assigned name to existingNodeNames so subsequent nodes get incremented names
          existingNodeNames.push(nodeName);

          return {
            type: FlowchartNodeChangeType.REPLACE as const,
            id: nodeToUpdate.id,
            data: {
              ...nodeToUpdate,
              taskDefinitionVersionSpec: newTaskDefinitionVersionSpec,
              nodeName,
              sections: newSections,
            },
          };
        });

        return {nodeChanges};
      }, isBlocking),
    [selectedNode, applyFlowchartChanges]
  );

  const adapterForSelectedNode = getAdapterForTaskSchemaNode(selectedNode);

  const selectedFieldName = useMemo(() => {
    if (!selectedFieldId || !selectedNode) {
      return null;
    }

    const fieldName = adapterForSelectedNode?.getFieldNameFromFieldId(selectedNode, selectedFieldId);
    if (fieldName == null) {
      // If field was removed, close the sidepanel (ex: removed map from start field from node in canvas)
      sidepanelManager.forceClose();
      return null;
    }
    return fieldName;
  }, [selectedFieldId, selectedNode, adapterForSelectedNode, sidepanelManager]);

  const metadataSidepanel = useMemo(
    () =>
      isFlowchartFeatureEnabled(
        flowchartFeatures,
        ProcessFlowchartEditorModeFeatureType.SHOW_FLOWCHART_SETTINGS_IF_DEFINED
      )
        ? PLUGIN_REGISTRY_INSTANCE.getPluginForFlowchartType(
            props.flowchartEditorConfigurator.flowchartTypeName
          ).metadataEditor
        : undefined,
    [flowchartFeatures, props.flowchartEditorConfigurator.flowchartTypeName]
  );

  const hasLeftSidebarContent = useMemo(
    () =>
      (!props.locked &&
        props.flowchartEditorConfigurator.hasFeature(
          ProcessFlowchartPlatformFeatureType.SHOW_ADD_NODE_SIDEPANEL
        )) ||
      metadataSidepanel != null ||
      (BenchlingConfig.WORKFLOWS_ENABLE_FLOWCHART_TRANSITIONS_SIDEPANEL_IN_HORIZONTAL_FLOWCHART &&
        props.flowchartEditorConfigurator.hasFeature(
          ProcessFlowchartPlatformFeatureType.SHOW_FLOWCHART_TRANSITIONS_SIDEPANEL
        )),
    [props.locked, props.flowchartEditorConfigurator, metadataSidepanel]
  );

  return (
    <div
      className={css(PROCESS_FLOWCHART_CONFIG_EDITOR_STYLES.flowchartEditorContentWrapper)}
      {...getTestProps({component: 'ProcessFlowchartConfigEditorContent', element: 'root'})}
    >
      <ReactFlowWrapper fullWidth={true} deferUntilMeasured={true}>
        <div
          className={css(PROCESS_FLOWCHART_CONFIG_EDITOR_STYLES.flowchartEditorContent)}
          ref={reactFlowWrapper}
        >
          <ProcessFlowchartConfigEditorLeftSidepanel
            taskDefinitionsResult={props.taskDefinitionsResult}
            allowedNodeTypes={allowedNodeTypes}
            applyFlowchartChanges={applyFlowchartChanges}
            setPlaceholderNodeBeingAdded={setPlaceholderNodeBeingAdded}
            options={interactiveFlowchartOptions}
            rootTaskSchema={props.rootTaskSchema}
            flowchartEditorConfigurator={props.flowchartEditorConfigurator}
            reactFlowWrapper={reactFlowWrapper}
            leftSidepanelRef={leftSidepanelRef}
            flowchartConfigMetadata={props.draftFlowchartConfigAndMetadata.metadata}
            metadataSidepanel={metadataSidepanel}
            ptdvIdToTaskSchemaId={ptdvIdToTaskSchemaId}
            allSchemasById={props.allSchemasById}
            flowchartTaskDefinitionId={props.flowchartTaskDefinitionId}
            flowchartConfigId={props.flowchartConfigId}
            rootAndOutputNodeConfig={
              props.editorMode === FlowchartEditorMode.SINGLE_TRANSITION_CANVAS
                ? {hasStartNode: startNode != null, hasEndNode: endNode != null}
                : undefined
            }
          />
          <div
            style={{
              flexGrow: 1,
              marginLeft: hasLeftSidebarContent ? FLOWCHART_SIDE_PANEL_WIDTH : 0,
              position: 'relative',
            }}
          >
            <ProcessFlowchartBanners
              locked={props.locked}
              editorMode={props.editorMode}
              lockErrorMessage={props.lockErrorMessage}
              validationErrors={props.validationErrors}
              limitsErrorMessage={
                hideDataFlowEdges
                  ? 'This flowchart is not showing all transitions in order to aid visualization performance.'
                  : undefined
              }
              infoBannerMessages={props.infoBannerMessages}
            />
            <CanvasContextProviders
              flowchartFeatures={flowchartFeatures}
              handlesContext={handlesContext}
              nodesContext={nodesContext}
              edgesContext={{edges: interactiveEdges}}
            >
              <ReactFlow
                id={`${props.flowchartConfigId}-${props.editorMode}`}
                {...defaultReactFlowProps}
                {...DEFAULT_FLOWCHART_PROPS}
                {...interactionProps}
                nodes={nodesToRender}
                nodeTypes={PROCESS_FLOWCHART_CONFIG_EDITOR_NODE_TYPES}
                edgeTypes={PROCESS_FLOWCHART_CONFIG_EDITOR_EDGE_TYPES}
                edges={edgesToRender}
                onNodesChange={onReactFlowNodeChangesOnly}
                onEdgesChange={onReactFlowEdgeChangesOnly}
                onConnect={handleConnect}
                onConnectStart={handleConnectStart}
                onConnectEnd={handleConnectEnd}
                onDragOver={dragOverHandler}
                // ReactFlow v12 has onBeforeDelete, since we are on v11 we need deletes to go through our own handlers
                // see handleDeleteKeyPress in useFlowchartState.ts for details
                deleteKeyCode={null}
                isValidConnection={validateConnection}
                onlyRenderVisibleElements={true}
              >
                <Background gap={SNAP_GRID} />
                <HorizontalControls
                  shiftLeft={isLeftSidepanelOpen ? LEFT_SIDEPANEL_WIDTH : 0}
                  additionalControls={
                    <ControlledAutoLayoutButton
                      flowchartData={{nodes: interactiveNodes, edges: interactiveEdges, getHandles}}
                      customLayoutSettings={getCustomLayoutSettings()}
                      setNodesAndEdges={setNodesAndEdgesViaChangesForLayout}
                      onAutolayoutComplete={(durationSeconds) => {
                        analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.CLICKED_AUTOLAYOUT_BUTTON, {
                          actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
                          flowchartType: props.flowchartEditorConfigurator.flowchartTypeName,
                          numNodes: interactiveNodes.length,
                          numEdges: interactiveEdges.length,
                          durationSeconds,
                        });
                      }}
                    />
                  }
                />
                <DefaultBackground />
              </ReactFlow>
            </CanvasContextProviders>
          </div>
          <ProcessFlowchartConfigEditorRightSidepanel
            rightSidepanelRef={rightSidepanelRef}
            onPanelSizeChange={setRightPanelSize}
            flowchartConfigId={props.flowchartConfigId}
            flowchartEditorConfigurator={props.flowchartEditorConfigurator}
            interactiveFlowchart={{nodes: interactiveNodes, edges: interactiveEdges}}
            flowchart={taskDefinitionVersionBasedFlowchart}
            size={adapterForSelectedNode?.getDisplayOptions().rightSidepanelSize ?? SidepanelSize.MEDIUM}
            titleOverride={adapterForSelectedNode
              ?.getDisplayOptions()
              .rightSidepanelNameOverride?.(selectedNode?.data.nodeName ?? '', selectedFieldName)}
            onUpdateTaskDefinitionVersion={onUpdateTaskDefinitionVersion}
            interactiveFlowchartOptions={interactiveFlowchartOptions}
            didInitialConfigHaveConditionalMapping={props.didInitialConfigHaveConditionalMapping}
            didInitialFlowchartHaveChainedRouters={props.didInitialFlowchartHaveChainedRouters}
            allSchemasById={props.allSchemasById}
            flowchartTaskSchema={props.rootTaskSchema}
            flowchartTaskDefinitionId={props.flowchartTaskDefinitionId}
            onChangeCustomMappingData={onChangeCustomMappingData}
            onChangeTransitionSource={onChangeTransitionSource}
            ownerToRegistryMap={props.ownerToRegistryMap}
            applyFlowchartChanges={applyFlowchartChanges}
            refetchFlowchartConfig={props.refetchFlowchartConfig}
            platformFlowchartFeatures={props.flowchartEditorConfigurator.platformFlowchartFeatures}
            liveStateChanges={props.liveStateChanges}
            locked={props.locked}
            taskDefinitionsForSidepanel={taskDefinitions}
            validationErrors={props.validationErrors}
          />
        </div>
      </ReactFlowWrapper>
    </div>
  );
}
